import { useState } from 'react';
import { ChannelWithId, updateChannelConfig } from '../lib/data';

/** Split a comma/newline list into trimmed non-empty entries. */
function parseList(s: string, sep: RegExp): string[] {
  return s.split(sep).map((x) => x.trim()).filter(Boolean);
}

/**
 * Channel config editor (R11/R12). Validates `threshold < frequency` client-side
 * (rules enforce it again server-side) and writes through the rules-guarded path,
 * which auto-generates an audit entry.
 */
export function ConfigForm({ channel }: { channel: ChannelWithId }) {
  const [frequency, setFrequency] = useState(channel.frequencyMinutes);
  const [threshold, setThreshold] = useState(channel.thresholdMinutes);
  const [grace, setGrace] = useState(channel.graceMinutes);
  const [probe, setProbe] = useState(channel.probeTemplate);
  const [scripted, setScripted] = useState(channel.scriptedReplies.join('\n'));
  const [mode, setMode] = useState(channel.validation.mode);
  const [required, setRequired] = useState(channel.validation.requiredKeywords.join(', '));
  const [rejected, setRejected] = useState(channel.validation.rejectedPhrases.join(', '));
  const [minLength, setMinLength] = useState(channel.validation.minLength);
  const [status, setStatus] = useState<string | null>(null);

  const error = threshold >= frequency ? 'Threshold must be below frequency.' : threshold <= 0 ? 'Threshold must be positive.' : null;

  const save = async () => {
    if (error) return;
    setStatus('saving…');
    try {
      await updateChannelConfig(channel.productId, channel.id, {
        frequencyMinutes: frequency,
        thresholdMinutes: threshold,
        graceMinutes: grace,
        probeTemplate: probe,
        scriptedReplies: parseList(scripted, /\n/),
        validation: {
          mode,
          requiredKeywords: parseList(required, /,/),
          rejectedPhrases: parseList(rejected, /,/),
          minLength,
        },
      });
      setStatus('saved ✓');
    } catch {
      setStatus('save failed — check permissions');
    }
  };

  return (
    <form className="config-form" onSubmit={(e) => { e.preventDefault(); save(); }}>
      <div className="row">
        <label>Frequency (min)<input type="number" min={1} value={frequency} onChange={(e) => setFrequency(Number(e.target.value))} /></label>
        <label>Threshold (min)<input type="number" min={1} value={threshold} onChange={(e) => setThreshold(Number(e.target.value))} /></label>
        <label>Grace (min)<input type="number" min={0} value={grace} onChange={(e) => setGrace(Number(e.target.value))} /></label>
      </div>
      <label>Probe template<textarea value={probe} maxLength={2000} onChange={(e) => setProbe(e.target.value)} /></label>
      <label>Scripted intake replies (one per line)<textarea value={scripted} onChange={(e) => setScripted(e.target.value)} /></label>
      <div className="row">
        <label>Match mode
          <select value={mode} onChange={(e) => setMode(e.target.value as 'all' | 'any')}>
            <option value="any">any</option>
            <option value="all">all</option>
          </select>
        </label>
        <label>Min length<input type="number" min={0} value={minLength} onChange={(e) => setMinLength(Number(e.target.value))} /></label>
      </div>
      <label>Required keywords (comma-separated)<input value={required} onChange={(e) => setRequired(e.target.value)} /></label>
      <label>Rejected phrases (comma-separated)<input value={rejected} onChange={(e) => setRejected(e.target.value)} /></label>
      {error && <p className="form-error">{error}</p>}
      <div className="row">
        <button type="submit" disabled={!!error}>Save config</button>
        {status && <span className="save-status">{status}</span>}
      </div>
    </form>
  );
}
